import React, { useState, useEffect, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { db, auth } from './firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import Select from 'react-select';
import './LinkedInTheme.css';

function SalesDashboard() {
  const { t } = useTranslation();
  const { userProfile } = useOutletContext();

  // State for creating orgs
  const [orgId, setOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // State for assigning users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({ hr: false, hm: false });
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(null);
  
  // State for fetching user's own organization name
  const [userOrgName, setUserOrgName] = useState('');
  
  // State for menu toggle
  const [activeMenuId, setActiveMenuId] = useState(null);
  const menuTimeoutRef = useRef(null);

  const toggleMenu = (orgId) => {
    setActiveMenuId(activeMenuId === orgId ? null : orgId);
  };

  const handleMenuEnter = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenuId(null);
    }, 300);
  };

  const fetchInitialData = async () => {
    try {
      // Fetch Orgs
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgs = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrganizations(orgs);

      // Fetch Users
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();
      const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getUsers`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (!usersResponse.ok) throw new Error('Failed to fetch users.');
      const usersData = await usersResponse.json();
      setUsers(usersData.users);

    } catch (err) {
      console.error("Error fetching initial data:", err);
      setCreateError(err.message); // Show error in the first form
    }
  };

  // Fetch user's organization name if they belong to one
  useEffect(() => {
    const fetchUserOrg = async () => {
      if (userProfile?.orgId) {
        // Check if we already have it in the loaded organizations list
        const foundOrg = organizations.find(o => o.id === userProfile.orgId);
        if (foundOrg) {
          setUserOrgName(foundOrg.name);
        }
      }
    };
    fetchUserOrg();
  }, [userProfile, organizations]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // When a user is selected, pre-fill the form with their data
  useEffect(() => {
    if (selectedUser) {
      const userRoles = selectedUser.profile?.roles || [];
      setSelectedRoles({
        hr: userRoles.includes('hr'),
        hm: userRoles.includes('hm'),
      });
      const userOrg = orgOptions.find(o => o.value === selectedUser.profile?.orgId);
      setSelectedOrg(userOrg || null);
    } else {
      // Clear form when user is deselected
      setSelectedRoles({ hr: false, hm: false });
      setSelectedOrg(null);
    }
  }, [selectedUser]);

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!orgId || !orgName) {
      setCreateError(t('salesDashboard.createOrgError'));
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t('organizationOnboarding.loginError'));
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/createOrganization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ orgId, orgName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('adminDashboard.defaultError'));
      }

      setCreateSuccess(t('salesDashboard.createOrgSuccess', { orgName }));
      setOrgId('');
      setOrgName('');
      fetchInitialData(); // Refresh the list
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedOrg) {
      setAssignError(t('salesDashboard.assignUserError'));
      return;
    }
    
    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t('organizationOnboarding.loginError'));
      const idToken = await user.getIdToken();

      // Start with existing roles and modify based on selection
      const existingRoles = new Set(selectedUser.profile?.roles || ['candidate']);
      if (selectedRoles.hr) {
        existingRoles.add('hr');
      } else {
        existingRoles.delete('hr');
      }
      if (selectedRoles.hm) {
        existingRoles.add('hm');
      } else {
        existingRoles.delete('hm');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateUserConfiguration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ 
          targetUserId: selectedUser.value, 
          orgId: selectedOrg.value,
          roles: Array.from(existingRoles)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('adminDashboard.updatingError'));
      }
      
      setAssignSuccess(t('salesDashboard.assignUserSuccess'));
      fetchInitialData(); // Refresh user list to show new roles
      setSelectedUser(null);

    } catch (err) {
      setAssignError(err.message);
    } finally {
      setAssignLoading(false);
    }
  };

  const userOptions = users.map(user => ({
    value: user.id,
    label: user.profile?.email || user.id,
    ...user
  }));

  const orgOptions = organizations.map(org => ({
    value: org.id,
    label: `${org.name} (${org.id})`
  }));

  const formatOptionLabel = ({ profile }) => (
    <div className="user-select-option">
      <div className="user-select-option__email">{profile?.email}</div>
      <div className="user-select-option__details">
        {t('salesDashboard.userOptionLabel', { 
          firstName: profile?.firstName || 'N/A', 
          lastName: profile?.lastName || '', 
          orgId: profile?.orgId || 'N/A', 
          roles: profile?.roles?.join(', ') || 'candidate' 
        })}
      </div>
    </div>
  );

  // Placeholder helper if userProfile images are missing
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  return (
    <div className="li-layout">
      <div className="li-container">
          
          {/* Left Sidebar */}
        <aside className="li-sidebar-left">
          {/* Profile Card */}
          <div className="li-card li-profile-card">
            <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
            <div className="li-card-content">
              <img 
                src={userProfile?.avatar_url || defaultAvatar} 
                alt="Profile" 
                className="li-avatar"
              />
              <div className="li-identity">
                <h3 className="li-name">
                  {userProfile?.firstName} {userProfile?.lastName || 'User'}
                </h3>
                <p className="li-headline">
                  {userProfile?.roles ? userProfile.roles.join(' | ').toUpperCase() : t('salesDashboard.noRoles')}
                  {userOrgName ? ` | ${userOrgName}` : ''}
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Column (Feed) */}
        <main className="li-main-feed">
          {/* Create Org "Post" Area */}
          <div className="li-card li-create-post-card">
             <div className="li-create-body">
                <h3>{t('adminDashboard.createOrgTitle')}</h3>
                <form onSubmit={handleCreateOrganization}>
                  <div className="li-form-row">
                    <input
                      type="text"
                      className="li-input"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      placeholder={t('adminDashboard.orgIdPlaceholder')}
                    />
                    <input
                      type="text"
                      className="li-input"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder={t('adminDashboard.orgNamePlaceholder')}
                    />
                  </div>
                  <div className="li-create-actions">
                    <button type="submit" className="li-btn-primary" disabled={createLoading}>
                      {createLoading ? t('adminDashboard.creatingButton') : t('adminDashboard.createButton')}
                    </button>
                  </div>
                  {createError && <div className="li-error-msg">{createError}</div>}
                  {createSuccess && <div className="li-success-msg">{createSuccess}</div>}
                </form>
             </div>
          </div>

          {/* Separator / Sort */}
          <div className="li-feed-sort">
             <hr />
             <span>{t('salesDashboard.sortBy')}: <strong>{t('salesDashboard.top')}</strong></span>
          </div>

          {/* Feed Items (Organizations) */}
          <div className="li-feed-items">
            {organizations.length === 0 ? (
               <div className="li-card li-empty-state">
                  <p>{t('salesDashboard.noOrgsFound')}</p>
               </div>
            ) : (
              organizations.map(org => (
                <div key={org.id} className="li-card li-feed-post">
                   <div className="li-post-header">
                      <div className="li-post-actor-image">
                         {/* Use a generic company icon */}
                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" data-supported-dps="24x24" fill="currentColor" width="48" height="48">
                            <path d="M4 2h16v20H4V2zm2 2v16h12V4H6zm2 2h2v2H8V6zm4 0h2v2h-2V6zm-4 4h2v2H8v-2zm4 0h2v2h-2v-2zm-4 4h2v2H8v-2zm4 0h2v2h-2v-2z"></path>
                         </svg>
                      </div>
                      <div className="li-post-meta">
                         <span className="li-post-title">{org.name}</span>
                         <span className="li-post-subtitle">ID: {org.id}</span>
                      </div>
                      <div 
                        style={{ position: 'relative', marginLeft: 'auto' }}
                        onMouseEnter={handleMenuEnter}
                        onMouseLeave={handleMenuLeave}
                      >
                        <button 
                            className="li-icon-btn-ghost" 
                            onClick={() => toggleMenu(org.id)}
                        >
                            •••
                        </button>
                        {activeMenuId === org.id && (
                            <div className="li-dropdown-menu li-action-menu">
                                <ul className="li-dropdown-list">
                                    <li>
                                        <Link to={`/organization-settings/${org.id}`} className="li-dropdown-link">
                                            {t('salesDashboard.editOrg', 'Edit Organization')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        )}
                      </div>
                   </div>
                   <div className="li-post-content">
                      <p>
                        <Trans i18nKey="salesDashboard.orgActiveMessage" values={{ name: org.name }}>
                          Organization <strong>{org.name}</strong> is active and ready for operations. Check the talent pool to see available candidates.
                        </Trans>
                      </p>
                   </div>
                   <div className="li-post-footer-actions">
                      <Link to={`/talent-pool/${org.id}`} className="li-action-btn">
                         {t('salesDashboard.viewTalentPool', 'View Talent Pool')}
                      </Link>
                   </div>
                </div>
              ))
            )}
          </div>
        </main>

        {/* Right Sidebar */}
        <aside className="li-sidebar-right">
          <div className="li-card li-news-card">
             <div className="li-card-header">
                <h3>{t('salesDashboard.assignUserTitle')}</h3>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" data-supported-dps="16x16" fill="currentColor" width="16" height="16">
                  <path d="M12 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm0 10H4V4h8v8z"></path>
                </svg>
             </div>
             <div className="li-card-body">
                <form onSubmit={handleAssignUser}>
                   <div className="li-form-stacked">
                      <label>{t('adminDashboard.userLabel')}</label>
                      <Select
                        options={userOptions}
                        value={selectedUser}
                        onChange={setSelectedUser}
                        formatOptionLabel={formatOptionLabel}
                        placeholder={t('salesDashboard.selectUser')}
                        isClearable
                        classNamePrefix="li-select"
                      />
                   </div>
                   <div className="li-form-stacked">
                      <label>{t('adminDashboard.orgLabel')}</label>
                      <Select
                        options={orgOptions}
                        value={selectedOrg}
                        onChange={setSelectedOrg}
                        placeholder={t('salesDashboard.selectOrg')}
                        isClearable
                        classNamePrefix="li-select"
                      />
                   </div>
                   
                   <div className="li-roles-group">
                      <span className="li-roles-label">{t('salesDashboard.billableRolesLabel')}</span>
                      <label className="li-checkbox-row">
                         <input type="checkbox" checked={selectedRoles.hr} onChange={(e) => setSelectedRoles({...selectedRoles, hr: e.target.checked})} />
                         <span>{t('salesDashboard.roleHR')}</span>
                      </label>
                      <label className="li-checkbox-row">
                         <input type="checkbox" checked={selectedRoles.hm} onChange={(e) => setSelectedRoles({...selectedRoles, hm: e.target.checked})} />
                         <span>{t('salesDashboard.roleHM')}</span>
                      </label>
                   </div>

                   <button type="submit" className="li-btn-outline-primary" disabled={assignLoading}>
                      {assignLoading ? t('salesDashboard.assigningButton') : t('salesDashboard.assignRoleButton')}
                   </button>

                   {assignError && <div className="li-error-msg-small">{assignError}</div>}
                   {assignSuccess && <div className="li-success-msg-small">{assignSuccess}</div>}
                </form>
             </div>
          </div>
          <div className="li-footer-links">
                <Link to="/about">{t('footer.about')}</Link>
                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                <span>{t('auth.footer.rights')}</span>
           </div>
        </aside>

      </div>
    </div>
  );
}

export default SalesDashboard;